import { useMemo } from 'react';
import { Box } from '@material-ui/core';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Product } from '@setvi/shared/interfaces';
import {
  SRoundedContainer,
  SAccordion,
  SButton,
  SText
} from '@setvi/shared/components';
import { useDialog } from '@setvi/shared/providers';

import { AILoader } from 'pages/products/components';
import {
  loadingSalesEnablementMessages,
  creatingSalesEnablementMessages
} from 'pages/products/components/ai/loader/messages';

import { ContentTitleContainer } from 'componentsV2/product/details/content/subpages';
import { useSalesEnablement } from './hook';
import Metadata from './components/metadata';
import { Container, MetadataMessage } from './styled';
import { NoSalesEnablement, SalesKitFeedback } from './components';

export interface SalesEnablementProps {
  product: Product;
}

export const MobileSalesEnablement = ({ product }: SalesEnablementProps) => {
  const flags = useFlags();

  const { openDialog, closeDialog } = useDialog();
  const {
    items,
    errorCode,
    activeItem,
    metadataFetched,
    initalLoading,
    expanded,
    isDisabled,
    isFetching,
    isToggleLoading,
    metadataErrorCode,
    regenerateAll,
    handleToggle,
    handleContentClick,
    handleRegeneration
  } = useSalesEnablement({ product });

  const currentItem = useMemo(() => {
    const res = items?.find(item => item._id === activeItem);
    return res;
  }, [items, activeItem]);

  const sortedArr = useMemo(
    () => items?.sort((a, b) => a.order - b.order),
    [items]
  );

  const salesEnablementMessages = useMemo(
    () =>
      isFetching
        ? loadingSalesEnablementMessages
        : creatingSalesEnablementMessages,
    [isFetching]
  );

  if (initalLoading)
    return (
      <Container>
        <AILoader messages={salesEnablementMessages} />
        {metadataFetched && (
          <MetadataMessage mt={3}>
            <SText size="sm">Metadata Fetched 🎉</SText>
          </MetadataMessage>
        )}
      </Container>
    );

  if (!items?.length && !currentItem) {
    return (
      <Container>
        <ContentTitleContainer
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
          <Box display="flex" gridGap="8px" flexWrap="wrap">
            {!!flags?.productMetadata && (
              <SButton
                onClick={handleToggle}
                loading={isToggleLoading}
                disabled={isToggleLoading}
                style={{
                  minHeight: 0,
                  borderRadius: 120
                }}>
                {isDisabled ? 'Enable' : 'Disable'}
              </SButton>
            )}
            <SalesKitFeedback product={product} />
          </Box>
        </ContentTitleContainer>

        <NoSalesEnablement
          disabled={isDisabled}
          errorCode={errorCode}
          metadataError={!!metadataErrorCode}
          onRetry={() => regenerateAll()}
        />
      </Container>
    );
  }

  return (
    <Container>
      <ContentTitleContainer
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
        <Box display="flex" gridGap="8px" flexWrap="wrap">
          {!!flags?.productMetadata && (
            <>
              <SButton
                onClick={handleToggle}
                loading={isToggleLoading}
                disabled={isToggleLoading}
                style={{
                  minHeight: 0,
                  borderRadius: 120
                }}>
                {isDisabled ? 'Enable' : 'Disable'}
              </SButton>
              <SButton
                variant="outlined"
                onClick={() =>
                  openDialog(
                    <Metadata
                      onClose={() => closeDialog('product-metadata')}
                      handleRegeneration={regenerateAll}
                      product={product}
                    />,
                    {
                      id: 'product-metadata'
                    }
                  )
                }
                style={{
                  minHeight: 0,
                  borderRadius: 120
                }}>
                Metadata
              </SButton>
            </>
          )}
          <SalesKitFeedback product={product} />
        </Box>
      </ContentTitleContainer>
      <Box display="flex" flexDirection="column" flex={1} pb={2} gridGap="16px">
        {sortedArr.map(item => (
          <Box display="flex" flexDirection="column" gridGap="4px">
            <Box p={2} borderRadius={16} style={{ backgroundColor: '#EEF0F2' }}>
              <SText weight="semibold" title={item.name} />
            </Box>
            {!item.loading &&
              item?.content?.map(i => (
                <SRoundedContainer
                  key={i?.id}
                  mb={2}
                  style={{ padding: '0 16px' }}>
                  <SAccordion
                    key={i.name}
                    title={i.name}
                    content={
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: i?.content }}
                      />
                    }
                    expanded={expanded === i.name}
                    onClick={name => {
                      handleContentClick(name, i?.name || '');
                    }}
                  />
                </SRoundedContainer>
              ))}

            {item?.loading && <AILoader messages={salesEnablementMessages} />}

            {!item?.content?.length && !item?.loading && (
              <NoSalesEnablement
                disabled={isDisabled}
                errorCode={errorCode}
                metadataError={!!metadataErrorCode}
                onRetry={() => handleRegeneration(activeItem || '')}
              />
            )}
          </Box>
        ))}
      </Box>
    </Container>
  );
};
